import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { itemsOf, type NavArea, type NavItem } from '../navigation.ts';

/** Busca "Ir para tela…" (Ctrl K): procura em todas as áreas que a pessoa pode ver. */
export function ScreenSearch({ areas, onClose }: { areas: NavArea[]; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const entries = useMemo(
    () =>
      areas.flatMap((area) =>
        itemsOf(area).map((item) => ({ item, label: t(item.key), area: t(area.key) })),
      ),
    [areas, t],
  );
  const normalize = (s: string) =>
    s
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
  const found = entries.filter((e) =>
    normalize(`${e.label} ${e.area}`).includes(normalize(query.trim())),
  );

  function go(item: NavItem) {
    onClose();
    void navigate({ to: item.to });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/20 px-4 pt-[12vh] backdrop-blur-[2px]"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.search')}
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-lg border border-rule bg-sheet shadow-[0_12px_32px_rgb(23_32_43/0.16)]"
      >
        <div className="flex items-center gap-2.5 border-b border-rule px-4">
          <Search size={18} strokeWidth={1.75} className="shrink-0 text-muted" aria-hidden />
          <input
            ref={inputRef}
            id="screen-search"
            value={query}
            placeholder={t('nav.searchPlaceholder')}
            aria-label={t('nav.searchPlaceholder')}
            onChange={(e) => {
              setQuery(e.target.value);
              setIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIndex((i) => Math.min(i + 1, found.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setIndex((i) => Math.max(i - 1, 0));
              }
              if (e.key === 'Enter' && found[index]) go(found[index].item);
            }}
            className="min-h-12 w-full bg-transparent text-sm text-ink outline-none placeholder:text-muted focus-visible:outline-none"
          />
          <kbd className="hidden shrink-0 rounded border border-rule bg-paper px-1.5 font-mono text-[11px] text-muted md:inline">
            Esc
          </kbd>
        </div>
        <ul className="max-h-80 overflow-y-auto p-1.5">
          {found.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted">{t('nav.searchEmpty')}</li>
          )}
          {found.map((e, i) => {
            const Icon = e.item.icon;
            const active = i === index;
            return (
              <li key={e.item.to}>
                <button
                  type="button"
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => go(e.item)}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm ${
                    active ? 'bg-paper text-ink' : 'text-ink'
                  }`}
                >
                  <Icon
                    size={18}
                    strokeWidth={1.75}
                    aria-hidden
                    className={`shrink-0 ${active ? 'text-brand-text' : 'text-muted'}`}
                  />
                  <span className="flex-1">{e.label}</span>
                  <span className="text-xs text-muted">{e.area}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
